import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import serverURL from "../../config";
import Loader from "../loader";


const getEmbedUrl = (url) => {
  if (!url) return url;
  const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/;
  const match = url.match(youtubeRegex);

  if (match?.[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }
  return url;
};

const buildHtmlDocument = (content) => `<!DOCTYPE html><html><head><style>body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; line-height: 1.6; color: #374151; padding: 1rem; margin: 0; word-wrap: break-word; } img { max-width: 100%; height: auto; }</style></head><body>${content}</body></html>`;

const LessonCard = () => {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState(null);
  const [lessonContent, setLessonContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const { setCustomTitle } = useOutletContext() || {};

  useEffect(() => {
    if (lessonData?.event_name && setCustomTitle) {
      setCustomTitle(lessonData.event_name);
    }
  }, [lessonData?.event_name, setCustomTitle]);

  const updateLessonProgress = useCallback(async (status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${serverURL}/lessons/${levelId}/progress`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (err) {
      console.error("Error updating lesson progress:", err);
    }
  }, [levelId]);

  const fetchLessonContent = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const lessonResponse = await axios.get(
        `${serverURL}/lessons/${levelId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLessonData(lessonResponse.data);
      setLessonContent(lessonResponse.data.items || []);

      if (lessonResponse.data.status === 'pending' || lessonResponse.data.status === null) {
        updateLessonProgress('in_progress');
      }
    } catch (err) {
      console.error("Error fetching lesson:", err);
      setError(err.response?.data?.message || "Failed to load lesson content");
      toast.error("Failed to load lesson");
    } finally {
      setLoading(false);
    }
  }, [levelId, updateLessonProgress]);

  useEffect(() => {
    fetchLessonContent();
  }, [fetchLessonContent]);

  const handleMarkComplete = async () => {
    try {
      await updateLessonProgress('completed');
      toast.success("Lesson marked as completed!");
      setLessonData((prev) => ({ ...prev, status: 'completed' }));
    } catch (err) {
      console.error("Error marking lesson complete:", err);
      toast.error("Failed to mark lesson as completed");
    }
  };

  const handleBack = () => {
    navigate('/events');
  };

  const handleCloseMenu = () => setIsOpen(false);
  const handleOpenMenu = () => setIsOpen(true);

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-campus-bg min-h-[78.5vh]">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center bg-campus-bg min-h-[78.5vh] p-6">
        <div className="text-campus-error text-xl font-semibold mb-4 text-center">{error}</div>
        <button
          onClick={handleBack}
          className="bg-campus-primary hover:bg-campus-primary-dark text-white px-6 py-2.5 rounded-pill hover:shadow-glow transition-all flex items-center gap-2 font-medium"
        >
          <FaArrowLeft /> Back to Events
        </button>
      </div>
    );
  }

  if (!lessonData) return null;

  return (
    <div className="min-h-screen bg-campus-bg">
      {!isOpen && (
        <div className="md:hidden bg-white border-b border-campus-border px-6 py-4 fixed top-16 left-0 right-0 z-40 shadow-sm">
          <button
            className="text-campus-text-primary focus:outline-none flex items-center gap-2"
            onClick={handleOpenMenu}
            aria-label="Open menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
            <span className="text-sm font-medium">Menu</span>
          </button>
        </div>
      )}

      {isOpen && (
        <button
          type="button"
          className="fixed top-16 inset-x-0 bottom-0 bg-black bg-opacity-50 z-40 md:hidden w-full cursor-default border-0 p-0"
          onClick={handleCloseMenu}
          aria-label="Close menu overlay"
        />
      )}

      <aside
        className={`${isOpen ? "fixed top-16 inset-x-0 bottom-0 z-50" : "hidden"} md:block md:fixed md:left-0 md:top-0 md:bottom-0 w-full md:w-72 bg-white border-r border-campus-border`}
      >
        <div className="h-full overflow-y-auto">
          <div className="p-4 md:p-6 space-y-3 pt-6 md:pt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-heading font-semibold text-campus-text-primary">Menu</h2>
              {isOpen && (
                <button
                  className="md:hidden text-campus-text-body hover:text-campus-text-primary text-xl p-1"
                  onClick={handleCloseMenu}
                  aria-label="Close menu"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              onClick={handleBack}
              className="w-full p-3 rounded-pill bg-red-100 hover:bg-red-200 border border-campus-border transition-all flex items-center gap-2 justify-center font-medium text-campus-text-body hover:text-campus-text-primary shadow-card hover:shadow-hover text-sm"
            >
              <FaArrowLeft className="text-sm" />
              <span>Back</span>
            </button>
            {lessonData.status !== 'completed' ? (
              <button
                onClick={handleMarkComplete}
                className="w-full p-3 rounded-pill bg-green-600 hover:bg-green-700 text-white font-semibold hover:shadow-hover transition-all flex items-center justify-center gap-2 text-sm mt-2"
              >
                <FaCheckCircle className="text-base" />
                <span>Complete</span>
              </button>
            ) : (
              <div className="w-full p-3 rounded-pill bg-green-50 text-green-700 font-semibold text-center border border-green-200 flex items-center justify-center gap-2 shadow-card text-sm mt-2">
                <FaCheckCircle className="text-base" />
                <span>Completed</span>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="md:ml-72 min-h-screen pt-32 md:pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 md:py-8">
          <header className="bg-gradient-to-r from-gray-500 to-gray-600 p-6 md:p-8 rounded-card mb-6 md:mb-8 text-white shadow-card">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-3">{lessonData.level_name}</h1>
            <div className="space-y-1 text-sm opacity-90">
              <p>Event: {lessonData.event_name}</p>
              <p>Institution: {lessonData.institution}</p>
            </div>
          </header>

          <div className="space-y-6 pb-8">
            <section className="bg-white p-6 md:p-8 border border-campus-border rounded-card shadow-card">
              <h2 className="text-xl sm:text-2xl font-heading font-bold mb-4 text-red-600">
                Lesson Overview
              </h2>
              <div className="space-y-4 text-campus-text-body leading-relaxed">
                <p>
                  Welcome to <strong className="text-campus-text-primary">{lessonData.level_name}</strong>! This lesson is part of the{" "}
                  <strong className="text-campus-text-primary">{lessonData.event_name}</strong> event.
                </p>
                <p>
                  Please review the content carefully and make sure you understand all the concepts
                  before marking this lesson as complete.
                </p>
              </div>
            </section>

            {lessonData.resources && lessonData.resources.length > 0 && (
              <section className="bg-white p-6 md:p-8 border border-campus-border rounded-card shadow-card">
                <h3 className="text-xl font-heading font-semibold mb-4 text-red-600">
                  Topics Covered
                </h3>
                <ul className="space-y-3">
                  {lessonData.resources.map((resource) => (
                    <li key={resource.category_name} className="text-campus-text-body">
                      <strong className="text-campus-text-primary block mb-1">
                        {resource.category_name}
                      </strong>
                      {resource.description && (
                        <p className="text-sm text-campus-text-body pl-4 border-l-2 border-gray-200">
                          {resource.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {lessonContent && lessonContent.length > 0 ? (
              lessonContent.map((item, index) => (
                <article key={item.id || index} className="bg-white p-6 md:p-8 border border-campus-border rounded-card shadow-card">
                  <h3 className="text-lg sm:text-xl font-heading font-semibold mb-3 text-red-600">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-campus-text-body mb-4 italic text-sm sm:text-base">
                      {item.description}
                    </p>
                  )}
                  <div className="mt-4">
                    {item.content_type === 'markdown' && (
                      <div className="prose prose-sm sm:prose max-w-none text-campus-text-body">
                        <ReactMarkdown>{item.content}</ReactMarkdown>
                      </div>
                    )}
                    {item.content_type === 'html' && (
                      <iframe
                        title={item.title}
                        className="w-full min-h-[420px] rounded-card border border-campus-border bg-white"
                        sandbox=""
                        srcDoc={buildHtmlDocument(item.content)}
                      />
                    )}
                    {item.content_type === 'video' && (
                      <div className="relative w-full rounded-card overflow-hidden" style={{ minHeight: '600px', height: '60vh' }}>
                        <iframe
                          src={getEmbedUrl(item.content)}
                          className="absolute top-0 left-0 w-full h-full border-0"
                          sandbox="allow-same-origin allow-scripts allow-presentation"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          title={item.title}
                        />
                      </div>
                    )}
                    {item.content_type === 'iframe' && (
                      <div className="relative w-full rounded-card overflow-hidden" style={{ minHeight: '800px', height: '80vh' }}>
                        <iframe
                          src={getEmbedUrl(item.content)}
                          className="absolute top-0 left-0 w-full h-full border-0"
                          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-presentation"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                          allowFullScreen
                          title={item.title}
                        />
                      </div>
                    )}
                    {item.content_type === 'pdf' && (
                      <div className="w-full rounded-card overflow-hidden border border-campus-border" style={{ height: '80vh', minHeight: '800px' }}>
                        <iframe
                          src={item.content}
                          className="w-full h-full border-0"
                          sandbox="allow-same-origin allow-scripts"
                          title={item.title}
                        />
                    </div>
                    )}
                    {item.content_type === 'text' && (
                      <p className="whitespace-pre-wrap text-campus-text-body leading-relaxed">
                        {item.content}
                      </p>
                    )}
                  </div>
                </article>
              ))
            ) : (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 md:p-8 rounded-card shadow-card">
                <p className="text-yellow-800 font-semibold mb-2 flex items-center gap-2">
                  <span>📝</span>
                  <span>No Content Available Yet</span>
                </p>
                <p className="text-campus-text-body">
                  The instructor hasn't added any content to this lesson yet. Please check back later.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LessonCard;
